import { z } from 'zod';
/**
 * Organisation workspace: purposeful contacts, visibility-classed notes,
 * tasks, milestones, service commitments and the aggregated activity
 * timeline. Notes and free text are scanned for prohibited content at the
 * boundary; note bodies never appear in audit snapshots or notifications.
 */
/* ------------------------------------------------ contacts: purpose/consent */
export const CONTACT_PURPOSES = [
    'commercial',
    'technical',
    'billing',
    'escalation',
    'data_protection',
];
export const ContactPurposeSchema = z.enum(CONTACT_PURPOSES);
export const CONSENT_BASES = ['contract', 'legitimate_interest', 'consent'];
export const ConsentBasisSchema = z.enum(CONSENT_BASES);
export const ContactConsentSchema = z
    .object({
    purpose: ContactPurposeSchema.default('commercial'),
    consentBasis: ConsentBasisSchema.default('contract'),
    consentNote: z.string().trim().max(500).optional(),
})
    .strict();
/* ---------------------------------------------------------------- notes */
export const NOTE_VISIBILITIES = ['internal', 'restricted', 'security'];
export const NoteVisibilitySchema = z.enum(NOTE_VISIBILITIES);
export const NoteCreateSchema = z
    .object({
    body: z.string().trim().min(3).max(8000),
    visibility: NoteVisibilitySchema.default('internal'),
})
    .strict();
export const NoteSchema = z.object({
    id: z.uuid(),
    organisationId: z.uuid(),
    authorUserId: z.uuid().nullable(),
    authorDisplayName: z.string().nullable(),
    visibility: NoteVisibilitySchema,
    body: z.string(),
    createdAt: z.iso.datetime(),
    archivedAt: z.iso.datetime().nullable(),
});
/* ---------------------------------------------------------------- tasks */
export const TASK_STATUSES = ['todo', 'in_progress', 'done', 'cancelled'];
export const TaskStatusSchema = z.enum(TASK_STATUSES);
export const TASK_PRIORITIES = ['low', 'normal', 'high'];
export const TaskCreateSchema = z
    .object({
    title: z.string().trim().min(3).max(200),
    description: z.string().trim().max(2000).optional(),
    ownerUserId: z.uuid().optional(),
    dueDate: z.iso.date().optional(),
    priority: z.enum(TASK_PRIORITIES).default('normal'),
})
    .strict();
export const TaskUpdateSchema = z
    .object({
    title: z.string().trim().min(3).max(200).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    status: TaskStatusSchema.optional(),
    ownerUserId: z.uuid().nullable().optional(),
    dueDate: z.iso.date().nullable().optional(),
    priority: z.enum(TASK_PRIORITIES).optional(),
    expectedVersion: z.number().int().min(1),
})
    .strict();
export const TaskSchema = z.object({
    id: z.uuid(),
    organisationId: z.uuid(),
    title: z.string(),
    description: z.string().nullable(),
    status: TaskStatusSchema,
    priority: z.enum(TASK_PRIORITIES),
    ownerUserId: z.uuid().nullable(),
    ownerDisplayName: z.string().nullable(),
    dueDate: z.iso.date().nullable(),
    overdue: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    version: z.number().int(),
});
/* ------------------------------------------------------------ milestones */
export const MILESTONE_STATUSES = ['planned', 'reached', 'missed'];
export const MilestoneUpsertSchema = z
    .object({
    title: z.string().trim().min(3).max(200),
    dueDate: z.iso.date(),
    status: z.enum(MILESTONE_STATUSES).default('planned'),
})
    .strict();
export const MilestoneSchema = z.object({
    id: z.uuid(),
    organisationId: z.uuid(),
    title: z.string(),
    dueDate: z.iso.date(),
    status: z.enum(MILESTONE_STATUSES),
    createdAt: z.iso.datetime(),
});
/* ---------------------------------------------------- service commitments */
/** Per-severity SLA minutes; falls back to DEFAULT_SLA where absent. */
export const SlaMinutesSchema = z
    .object({
    acknowledgeMinutes: z.number().int().min(5).max(20160),
    resolveMinutes: z.number().int().min(15).max(40320),
})
    .strict();
export const CommitmentUpsertSchema = z
    .object({
    supportTier: z.string().trim().min(2).max(60),
    sla: z
        .object({
        s1: SlaMinutesSchema.optional(),
        s2: SlaMinutesSchema.optional(),
        s3: SlaMinutesSchema.optional(),
        s4: SlaMinutesSchema.optional(),
    })
        .strict(),
    effectiveFrom: z.iso.date(),
    notes: z.string().trim().max(1000).optional(),
})
    .strict();
export const CommitmentSchema = z.object({
    id: z.uuid(),
    organisationId: z.uuid(),
    supportTier: z.string(),
    sla: z.record(z.string(), SlaMinutesSchema),
    effectiveFrom: z.iso.date(),
    notes: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/* ---------------------------------------------------------------- timeline */
export const TIMELINE_KINDS = [
    'lifecycle',
    'onboarding',
    'task',
    'milestone',
    'note',
    'case',
    'approval',
    'operator_action',
];
export const TimelineEntrySchema = z.object({
    id: z.string(),
    kind: z.enum(TIMELINE_KINDS),
    title: z.string(),
    detail: z.string().nullable(),
    actorDisplayName: z.string().nullable(),
    occurredAt: z.iso.datetime(),
    linkPath: z.string().nullable(),
});
//# sourceMappingURL=workspace.js.map